import React from "react";
import {
  Modal,
  Button,
  TextInput,
  Select,
  Stack,
  Title,
  Group,
  Divider,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import ModalHeader from "components/ModalHeader";
import { useAddRolePermission, RolePermission } from "api/role/addRole";
import { getCompletePermissions } from "utils/defaultPermissions";

const createRoleSchema = z.object({
  role: z.string().min(1, "Role name is required"),
  parentRole: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof createRoleSchema>;

interface CreateRoleModalProps {
  opened: boolean;
  onClose: () => void;
  roles: RolePermission[];
  organizationId: string;
}

export default function CreateRoleModal({
  opened,
  onClose,
  roles,
  organizationId,
}: CreateRoleModalProps) {
  const { mutate, isLoading } = useAddRolePermission();

  const form = useForm<FormValues>({
    initialValues: {
      role: "",
      parentRole: null,
    },
    validate: zodResolver(createRoleSchema),
  });

  const handleSubmit = (values: FormValues) => {
    const permissions = getCompletePermissions(false);

    mutate(
      {
        role: values.role,
        organizationId,
        permissions,
        parentRole: values.parentRole || undefined,
      },
      {
        onSuccess: () => {
          form.reset();
          onClose();
        },
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      centered
      withCloseButton={false}
      overlayOpacity={0.5}
      transitionDuration={300}
    >
      <ModalHeader
        title="Create New Role"
        onClose={onClose}
        ariaLabel="Close create role modal"
      />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="md">
          <TextInput
            label="Role Name"
            placeholder="Enter role name (e.g., Manager, Analyst)"
            {...form.getInputProps("role")}
          />

          <Select
            label="Parent Role (Optional)"
            placeholder="Select a parent role or leave empty for root"
            data={[
              { value: "", label: "None (Root Level)" },
              ...roles.map((r) => ({
                value: r.role,
                label: r.role,
              })),
            ]}
            value={form.values.parentRole || ""}
            onChange={(val) => form.setFieldValue("parentRole", val || null)}
          />

          <Divider my="sm" />

          <Group position="right">
            <Button color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="blue" loading={isLoading}>
              Create Role
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
